import { CSSProperties, useEffect, useRef, useState } from 'react';
import { registerModalBackHandler } from '../../../lib/native';
import { Button } from '../core/Button';
import { Input } from '../forms/Input';
import { Select } from '../forms/Select';
import { TagEditor } from '../forms/TagEditor';
import { ToastIllustration } from './ToastIllustration';
import type { StructuredPortrait } from '../../../types';
import { viewportHeight } from '../../../lib/viewport';
import { portraitBranches } from '../../../lib/portraitBranches';

export interface TripSummaryModalProps {
  /** The extracted portrait to review — fields not shown here (keywords, social) are carried through unedited. */
  portrait: StructuredPortrait;
  /** Fired with the edited portrait when the traveler taps "Find my host". */
  onSubmit: (edited: StructuredPortrait) => void;
  /** Disables the form and shows a spinner on the CTA while the final finalize call runs. */
  submitting?: boolean;
  /** Inline error shown above the CTA (e.g. finalize failed) — the modal stays open so they can retry. */
  error?: string | null;
  /** Optional close affordance. Omit to make the review a required step. */
  onClose?: () => void;
  /** Cities Sekretly can actually place a traveler in (active `destinations`
   *  rows) — the "Add a city" control only ever offers these, never free
   *  text, since anything else can't be matched. Omit/empty hides the "Add a
   *  city" row entirely rather than falling back to free text. */
  availableCities?: string[];
  /** Walking options (two-phase chat), worded for the traveler's group. Omit to show the saved answer read-only. */
  walkingOptions?: { slug: string; label: string }[];
  style?: CSSProperties;
}

/** One destination leg — replaces the old flat `destinations: string[]` + single `start_date`/`trip_days`. */
interface DestinationLeg {
  city: string;
  startDate: string;
  days: number;
}

type Pick = { label: string; slug?: string; custom?: string };

/** What a quick-answer pick reads as: the traveler's own words for "Other", else its label. */
const pickText = (p: Pick) => p.custom ?? p.label;

/** Edited tags back to picks: kept tags keep their slug; new ones are the traveler's own words. */
function picksFromTags<T extends Pick>(tags: string[], original: T[]): Pick[] {
  return tags.map((tag) => original.find((o) => pickText(o).toLowerCase() === tag.toLowerCase()) ?? { label: 'Other', custom: tag });
}

const KEYFRAMES = `
@keyframes sk-summary-up{from{transform:translateY(40px);opacity:0}to{transform:translateY(0);opacity:1}}
@keyframes sk-summary-fade{from{opacity:0}to{opacity:1}}
`;

const FIELD_LABEL_STYLE: CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', fontWeight: 500, color: 'var(--text-soft)',
};

function SectionHeader({ icon, label }: { icon: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <i className={`ti ti-${icon}`} style={{ color: 'var(--accent)', fontSize: 'calc(13px * var(--text-scale))' }} />
      <span style={{ color: 'var(--text-dim)', fontSize: 'var(--fs-caption)', fontWeight: 500 }}>{label}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
    </div>
  );
}

function paceBtnStyle(active: boolean): CSSProperties {
  return {
    fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', fontWeight: 500,
    padding: '5px 14px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
    transition: 'background .15s, color .15s, border-color .15s',
    border: active ? '1px solid var(--text)' : '1px solid var(--line)',
    background: active ? 'var(--text)' : 'var(--surface)',
    color: active ? 'var(--bg)' : 'var(--text-soft)',
  };
}

/** Prefers the real per-city breakdown when Alma (or a prior edit) produced one.
 *  Only bridges the legacy flat shape (single start_date/trip_days, destinations:
 *  string[]) — dumping all days onto the first city — when destination_days is
 *  absent, since that's a lossy last resort, not the normal path. */
function legsFromPortrait(portrait: StructuredPortrait): DestinationLeg[] {
  if (portrait.destination_days?.length) {
    return portrait.destination_days.map((d) => ({
      city: d.city,
      startDate: d.start_date ?? '',
      days: d.days,
    }));
  }
  const cities = portrait.destinations?.length ? portrait.destinations : [''];
  return cities.map((city, i) => ({
    city,
    startDate: i === 0 ? (portrait.start_date ?? '') : '',
    days: i === 0 ? (portrait.trip_days ?? 1) : 1,
  }));
}

/** Today in YYYY-MM-DD, local time — matches what a <input type="date"> yields. */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Add n days to a YYYY-MM-DD date, returning YYYY-MM-DD. */
function addDaysISO(date: string, n: number): string {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Total calendar days spanned by the trip: from the earliest dated leg's
 * start to the latest dated leg's end, inclusive. A day between two cities
 * (a transit day) is in the span but in no city, so this is NOT a sum of each
 * leg's own day count. A leg with no date of its own (a city added without
 * picking a date yet) has nowhere to sit in that span, so its days are
 * just added on top. Mirrors `spanDaysFromLegs` in alma-finalize.
 */
function totalTripDays(legs: DestinationLeg[]): number {
  const dated = legs.filter((l) => l.startDate);
  const undatedTotal = legs.filter((l) => !l.startDate).reduce((sum, l) => sum + l.days, 0);
  if (dated.length === 0) return undatedTotal || legs.reduce((sum, l) => sum + l.days, 0);
  let minStart = dated[0].startDate;
  let maxEnd = addDaysISO(dated[0].startDate, dated[0].days - 1);
  for (const leg of dated) {
    if (leg.startDate < minStart) minStart = leg.startDate;
    const end = addDaysISO(leg.startDate, leg.days - 1);
    if (end > maxEnd) maxEnd = end;
  }
  const spanMs = new Date(maxEnd + 'T12:00:00Z').getTime() - new Date(minStart + 'T12:00:00Z').getTime();
  return Math.round(spanMs / 86400000) + 1 + undatedTotal;
}

/** Two cities can't share a day: each must start after the previous one ends. A day left out between two cities is fine. */
function legsOverlap(legs: DestinationLeg[]): boolean {
  const dated = legs
    .filter((l) => l.startDate)
    .map((l) => ({ start: l.startDate, end: addDaysISO(l.startDate, l.days - 1) }))
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  return dated.some((l, i) => i > 0 && l.start <= dated[i - 1].end);
}

/** Dated cities in the order they are visited; a city with no date yet goes last. */
function inVisitingOrder(legs: DestinationLeg[]): DestinationLeg[] {
  const dated = legs.filter((l) => l.startDate).sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0));
  return [...dated, ...legs.filter((l) => !l.startDate)];
}

/**
 * TripSummaryModal — shown after Alma's intake closes. Groups what she
 * learned into editable sections (Trip details / How Alma sees you / Taste map)
 * so the traveler can correct anything before host matching runs on it.
 *
 * Destinations are now per-city: each city carries its own start date and day
 * count (a multi-city trip no longer forces one shared window). On submit this
 * still populates the legacy flat `start_date` (earliest leg) and `trip_days`
 * (sum of legs) fields so every existing consumer (host availability matching,
 * payments, payouts, admin, itinerary editor) keeps working unchanged, and adds
 * the per-city breakdown as `destination_days`.
 */
export function TripSummaryModal({ portrait, onSubmit, submitting = false, error, onClose, availableCities = [], walkingOptions, style: styleProp }: TripSummaryModalProps) {
  const [rawSummary, setRawSummary] = useState(portrait.raw_summary ?? '');
  const [tripName, setTripName] = useState(portrait.trip_name ?? '');
  const [legs, setLegs] = useState<DestinationLeg[]>(legsFromPortrait(portrait));
  const [newCity, setNewCity] = useState('');
  const remainingCities = availableCities.filter(
    (c) => !legs.some((l) => l.city.toLowerCase() === c.toLowerCase()),
  );
  const [pace, setPace] = useState<StructuredPortrait['pace']>(portrait.pace ?? 'medium');
  const [styleTags, setStyleTags] = useState<string[]>(portrait.style ?? []);
  const [interests, setInterests] = useState<string[]>(portrait.interests ?? []);
  const [dealbreakers, setDealbreakers] = useState<string[]>(portrait.dealbreakers ?? []);
  // Two-phase chat answers (absent on the classic journey).
  const [priorityTags, setPriorityTags] = useState<string[]>(portrait.priorities?.map(pickText) ?? []);
  const branches = portraitBranches(portrait);
  const [branchTags, setBranchTags] = useState<string[][]>(branches.map((b) => b.picks.map(pickText)));
  const [walkingSlug, setWalkingSlug] = useState(portrait.walking_comfort?.slug ?? '');

  const summaryRef = useRef<HTMLTextAreaElement>(null);
  const resizeSummary = () => {
    const el = summaryRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(resizeSummary, []);

  // Android hardware back button closes this modal instead of navigating
  // the route underneath it — only when a close affordance exists.
  useEffect(() => {
    if (!onClose) return;
    return registerModalBackHandler(onClose);
  }, [onClose]);

  function updateLeg(city: string, patch: Partial<DestinationLeg>) {
    setLegs(prev => prev.map(l => (l.city === city ? { ...l, ...patch } : l)));
  }

  function removeLeg(city: string) {
    setLegs(prev => prev.filter(l => l.city !== city));
  }

  function addCity() {
    const name = newCity.trim();
    // Only ever add a city Sekretly actually serves — the picker only offers
    // these, but re-checking here guards against a stale/leftover value too.
    if (!name || !remainingCities.some((c) => c.toLowerCase() === name.toLowerCase())) return;
    setLegs(prev => [...prev, { city: name, startDate: '', days: 1 }]);
    setNewCity('');
  }

  const totalDays = totalTripDays(legs);
  const earliestStart = legs.map(l => l.startDate).filter(Boolean).sort()[0];
  // A trip can't start in the past. This used to be unguarded, so a date
  // resolved to the wrong year sailed through review, got paid for, and the
  // trip was born already "over" — auto-completing itself and disappearing
  // from the host's board before it ever happened.
  const today = todayISO();
  const pastLegs = legs.filter((l) => l.startDate && l.startDate < today);
  const hasPastDate = pastLegs.length > 0;
  const overlapping = legsOverlap(legs);
  // A host can only be matched and a trip priced once we know where and
  // when. Without these the button used to submit anyway, and an empty
  // portrait went on to be booked and paid for (1 Oct).
  const missingCity = !legs.some((l) => l.city.trim() !== '');
  const missingDate = !earliestStart;
  const incomplete = missingCity || missingDate;

  function handleSubmit() {
    if (submitting || hasPastDate || overlapping || incomplete) return;
    onSubmit({
      ...portrait,
      trip_name: tripName.trim(),
      raw_summary: rawSummary.trim(),
      destinations: inVisitingOrder(legs).map(l => l.city).filter(Boolean),
      destination_days: inVisitingOrder(legs).filter(l => l.city).map(l => ({ city: l.city, start_date: l.startDate || undefined, days: l.days })),
      start_date: earliestStart || portrait.start_date,
      trip_days: totalDays || portrait.trip_days,
      pace,
      style: styleTags,
      interests,
      dealbreakers,
      ...(portrait.priorities
        ? { priorities: picksFromTags(priorityTags, portrait.priorities).map((p, i) => ({ rank: i + 1, ...p })) }
        : {}),
      ...(branches.length
        ? { branch_picks: branches.map((b, i) => ({ ...b, picks: picksFromTags(branchTags[i] ?? [], b.picks) })) }
        : {}),
      ...(portrait.walking_comfort
        ? { walking_comfort: walkingOptions?.find((o) => o.slug === walkingSlug) ?? portrait.walking_comfort }
        : {}),
    });
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        animation: 'sk-summary-fade .2s ease both',
      }}
    >
      <style>{KEYFRAMES}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Your taste map and trip details"
        style={{
          width: '100%', maxWidth: 560, background: 'var(--surface)',
          border: '1px solid var(--line)', borderRadius: 18, overflow: 'hidden',
          fontFamily: 'var(--font-sans)', maxHeight: viewportHeight(0.92), display: 'flex', flexDirection: 'column',
          animation: 'sk-summary-up .3s cubic-bezier(.16,.8,.3,1) both',
          ...styleProp,
        }}
      >
        {/* Header */}
        <div style={{ padding: '36px 28px 24px', borderBottom: '1px solid var(--line)', position: 'relative' }}>
          {onClose ? (
            <button className="sk-hit"
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                position: 'absolute', top: 20, right: 20, width: 30, height: 30, borderRadius: '50%',
                background: 'var(--bg)', border: '1px solid var(--line)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}
            >
              <i className="ti ti-x" style={{ color: 'var(--text-dim)', fontSize: 'calc(15px * var(--text-scale))' }} />
            </button>
          ) : null}
          <ToastIllustration height={96} style={{ color: 'var(--text)', marginBottom: 20 }} />
          <div style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)', fontSize: 'var(--fs-page-title)', lineHeight: 'var(--lh-page-title)', letterSpacing: 'var(--ls-page-title)', textWrap: 'pretty', maxWidth: 400 }}>
            Your taste is mapped
          </div>
          <div style={{ color: 'var(--text-soft)', fontSize: 'var(--fs-footnote)', lineHeight: 1.55, marginTop: 8, textWrap: 'pretty' }}>
            Take a look, fix anything before getting matched.
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 28, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 36 }}>

          {/* Trip details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SectionHeader icon="map-pin" label="Trip details" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={FIELD_LABEL_STYLE}>Trip name</label>
              <Input value={tripName} onChange={(e) => setTripName(e.target.value)} placeholder="A short name for this trip" />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <label style={FIELD_LABEL_STYLE}>Destinations</label>
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-dim)' }}>{totalDays} day{totalDays === 1 ? '' : 's'} total</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {legs.map((leg) => (
                  // Two lines, not one: city + date + stepper + remove needed
                  // ~290px of fixed-width controls on a single row, more than a
                  // phone has here — the city was squeezed to nothing and the
                  // stepper/remove pushed off the edge.
                  <div key={leg.city} data-testid="destination-leg" style={{ display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '10px 10px 12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-footnote)', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{leg.city}</span>
                      <button className="sk-hit" type="button" onClick={() => removeLeg(leg.city)} aria-label={`Remove ${leg.city}`} style={{ flexShrink: 0, width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--text-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="ti ti-x" style={{ fontSize: 'calc(13px * var(--text-scale))' }} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="date"
                        min={today}
                        value={leg.startDate}
                        onChange={(e) => updateLeg(leg.city, { startDate: e.target.value })}
                        aria-label={`Start date in ${leg.city}`}
                        className="sk-date-input"
                        // appearance:none — iOS's native date control otherwise
                        // keeps its own intrinsic width and won't shrink to fit.
                        style={{ flex: 1, minWidth: 0, minHeight: 34, WebkitAppearance: 'none', appearance: 'none', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-field)', padding: '6px 10px', outline: 'none' }}
                      />
                      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <button className="sk-hit" type="button" onClick={() => updateLeg(leg.city, { days: Math.max(1, leg.days - 1) })} aria-label="Fewer days" style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--line)', background: 'transparent', color: 'var(--text-soft)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="ti ti-minus" style={{ fontSize: 'calc(12px * var(--text-scale))' }} />
                        </button>
                        <span style={{ fontSize: 'var(--fs-footnote)', color: 'var(--text-soft)', minWidth: 52, textAlign: 'center' }}>{leg.days} day{leg.days === 1 ? '' : 's'}</span>
                        <button className="sk-hit" type="button" onClick={() => updateLeg(leg.city, { days: leg.days + 1 })} aria-label="More days" style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--line)', background: 'transparent', color: 'var(--text-soft)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="ti ti-plus" style={{ fontSize: 'calc(12px * var(--text-scale))' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {remainingCities.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg)', border: '1px dashed var(--line)', borderRadius: 'var(--r-sm)', padding: '10px 10px 10px 14px' }}>
                    <select
                      value={newCity}
                      onChange={(e) => setNewCity(e.target.value)}
                      aria-label="City to add"
                      style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', color: newCity ? 'var(--text)' : 'var(--text-dim)', padding: '4px 2px' }}
                    >
                      {/* Short enough not to clip on a phone; the list itself
                          only ever offers cities Sekretly operates in. */}
                      <option value="">Add another city</option>
                      {remainingCities.map((c) => (
                        <option key={c} value={c} style={{ color: 'var(--text)' }}>{c}</option>
                      ))}
                    </select>
                    <button className="sk-hit" type="button" onClick={addCity} disabled={!newCity} style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', fontWeight: 500, color: 'var(--text-soft)', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-pill)', padding: '5px 12px', cursor: newCity ? 'pointer' : 'not-allowed', opacity: newCity ? 1 : 0.5 }}>
                      Add
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* How Alma sees you */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <SectionHeader icon="sparkles" label="How Alma sees you" />
            {portrait.archetype ? (
              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 'calc(20px * var(--text-scale))', lineHeight: 1.25, letterSpacing: '-0.01em', color: 'var(--text)' }}>
                {portrait.archetype}
              </div>
            ) : null}
            <textarea
              ref={summaryRef}
              value={rawSummary}
              onChange={(e) => { setRawSummary(e.target.value); resizeSummary(); }}
              style={{
                width: '100%', fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-subhead)', lineHeight: 1.55, color: 'var(--text)',
                background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '14px 16px',
                outline: 'none', resize: 'none', overflow: 'hidden', transition: 'border-color .2s, box-shadow .2s',
              }}
            />
            <div style={{ color: 'var(--text-dim)', fontSize: 'var(--fs-caption)', textAlign: 'right' }}>Edit anything that doesn't sound like you.</div>
          </div>

          {/* Taste map */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SectionHeader icon="compass" label="Taste map" />

            {portrait.priorities ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={FIELD_LABEL_STYLE}>What matters most, in order</label>
                <TagEditor tags={priorityTags} onChange={setPriorityTags} placeholder="e.g. live music" />
              </div>
            ) : null}
            {branches.map((branch, i) => (
              <div key={branch.question_key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={FIELD_LABEL_STYLE}>{branch.title ?? 'Your picks'}</label>
                <TagEditor
                  tags={branchTags[i] ?? []}
                  onChange={(tags) => setBranchTags((all) => all.map((t, j) => (j === i ? tags : t)))}
                  placeholder="Add another"
                />
              </div>
            ))}
            {portrait.walking_comfort ? (
              walkingOptions?.length ? (
                <Select
                  label="How much walking is comfortable"
                  value={walkingSlug}
                  onChange={(e) => setWalkingSlug(e.target.value)}
                  options={walkingOptions.map((o) => ({ value: o.slug, label: o.label }))}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={FIELD_LABEL_STYLE}>How much walking is comfortable</span>
                  <span style={{ fontSize: 'var(--fs-footnote)', color: 'var(--text)' }}>{portrait.walking_comfort.label}</span>
                </div>
              )
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={FIELD_LABEL_STYLE}>Style</label>
              <TagEditor tags={styleTags} onChange={setStyleTags} placeholder="e.g. local, slow mornings" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={FIELD_LABEL_STYLE}>Interests</label>
              <TagEditor tags={interests} onChange={setInterests} placeholder="e.g. food, art, nightlife" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={FIELD_LABEL_STYLE}>Things to avoid</label>
              <TagEditor tags={dealbreakers} onChange={setDealbreakers} placeholder="e.g. crowds, early starts" />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={FIELD_LABEL_STYLE}>Pace</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['slow', 'medium', 'fast'] as const).map((p) => (
                  <button className="sk-hit" key={p} type="button" onClick={() => setPace(p)} style={paceBtnStyle(pace === p)}>
                    {p === 'slow' ? 'Slow' : p === 'medium' ? 'Medium' : 'Fast'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '22px 28px 26px', borderTop: '1px solid var(--line)' }}>
          {hasPastDate ? (
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', color: 'rgb(var(--crimson))', margin: '0 0 12px' }}>
              {pastLegs.length === 1
                ? `Your ${pastLegs[0].city} date is in the past — pick the date you're actually travelling.`
                : "Some of your dates are in the past — pick the dates you're actually travelling."}
            </p>
          ) : overlapping ? (
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', color: 'rgb(var(--crimson))', margin: '0 0 12px' }}>
              Two of your cities share a day. Each city must start after the previous one ends. A travel day in between can stay empty.
            </p>
          ) : incomplete ? (
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', color: 'var(--text-soft)', margin: '0 0 12px' }}>
              {missingCity && missingDate
                ? 'Add where and when you’re travelling to find your host.'
                : missingCity ? 'Add the city you’re travelling to.' : 'Add the date your trip starts.'}
            </p>
          ) : error ? (
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', color: 'rgb(var(--crimson))', margin: '0 0 12px' }}>{error}</p>
          ) : null}
          <Button variant="primary" size="lg" fullWidth loading={submitting} disabled={hasPastDate || overlapping || incomplete} onClick={handleSubmit}>
            Find my host
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TripSummaryModal;
